import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp, FaSearch } from "react-icons/fa";
import { TbFilterOff } from "react-icons/tb";

const FilterComponent = ({ role, onReset, open, handleApplyFilters }) => {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [panelHeight, setPanelHeight] = useState("0px");
  const contentRef = useRef(null);
  const advancedContentRef = useRef(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    booking_type: "",
    charter_frequency: "",
    charter_type: "",
  });

  const statusOptions = [
    { value: "active", label: t("active") },
    { value: "cancelled", label: t("cancelled") },
    { value: "completed", label: t("completed") },
    { value: "expired", label: t("expired") },
  ];

  const bookingTypeOptions = [
    { value: "bus_charter", label: `${t("bus")} ${t("charter")}` },
    {
      value: "busing_subscription",
      label: `${t("busing")} ${t("subscription")}`,
    },
  ];

  const charterFrequencyOptions = [
    { value: "one_time", label: `${t("one")} ${t("time")}` },
    { value: "recurring", label: t("recurring") },
  ];

  const charterTypeOptions = [
    { value: "outgoing_trip", label: `${t("outgoing")} ${t("trip")}` },
    { value: "return_trip", label: `${t("return")} ${t("trip")}` },
    { value: "round_trip", label: `${t("round")} ${t("trip")}` },
  ];

  // Update panel height when open or showAdvanced changes
  useEffect(() => {
    if (open && contentRef.current) {
      if (showAdvanced) {
        // Calculate total height including advanced filters
        const baseHeight = contentRef.current.scrollHeight;
        const advancedHeight = advancedContentRef.current?.scrollHeight || 0;
        setPanelHeight(`${baseHeight + advancedHeight}px`);
      } else {
        setPanelHeight(`${contentRef.current.scrollHeight}px`);
      }
    } else {
      setPanelHeight("0px");
    }
  }, [open, showAdvanced, filters]);

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      status: "",
      booking_type: "",
      charter_frequency: "",
      charter_type: "",
    };
    setFilters(resetFilters);
    setShowAdvanced(false);
    if (onReset) onReset();
  };

  const handleAdvancedToggle = () => {
    setShowAdvanced(!showAdvanced);
  };

  return (
    <>
      {(filters.search ||
        filters.status ||
        filters.booking_type ||
        filters.charter_frequency ||
        filters.charter_type) && (
        <button
          type="button"
          className="btn btn-outline-danger ms-auto mb-2 d-flex align-items-center gap-2"
          onClick={handleReset}
        >
          <TbFilterOff /> {t("btns.clear")} {t("btns.filters")}
        </button>
      )}

      {/* Filter Panel */}
      <div
        ref={contentRef}
        className="custom-collapse mb-4"
        style={{
          maxHeight: panelHeight,
          transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="filter-panel">
          <div className="filter-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2 text-primary"></i>
              {t("filter")} {t("reservations")}
            </h5>
          </div>

          <div className="filter-body">
            {/* Quick Search */}
            <div className="quick-search-section mb-4">
              <div className="search-box-wrapper">
                <label className="form-label fw-semibold">
                  {t("quick")} {t("search")}
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control ps-2"
                    placeholder={t("placeholder-search")}
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Status Filter - Always Visible */}
            <div className="status-filter-section mb-4">
              <label className="form-label fw-semibold">
                <i className="fas fa-tag me-2"></i>
                {t("status")}
              </label>
              <div className="status-buttons">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`status-btn ${
                      filters.status === option.value ? "active" : ""
                    }`}
                    onClick={() =>
                      handleFilterChange(
                        "status",
                        filters.status === option.value ? "" : option.value,
                      )
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="advanced-toggle mb-3">
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 d-flex align-items-center"
                onClick={handleAdvancedToggle}
              >
                {showAdvanced ? (
                  <>
                    <FaChevronUp className="me-2" />
                    <span>
                      {t("hide")} {t("advanced")} {t("filters")}
                    </span>
                  </>
                ) : (
                  <>
                    <FaChevronDown className="me-2" />
                    <span>
                      {t("show")} {t("advanced")} {t("filters")}
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* Advanced Filters */}
            <div
              ref={advancedContentRef}
              className={`advanced-filters ${showAdvanced ? "show" : ""}`}
              style={{
                maxHeight: showAdvanced ? "500px" : "0px",
                overflow: "hidden",
                transition: "max-height 0.4s ease",
              }}
            >
              <div className="row g-3">
                {/* Booking Type */}
                {/* <div className="col-md-6">
                  <div className="filter-group">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-calendar-check me-2"></i>
                      {t("booking")} {t("type")}
                    </label>
                    <select
                      className="form-select"
                      value={filters.booking_type}
                      onChange={(e) =>
                        handleFilterChange("booking_type", e.target.value)
                      }
                    >
                      <option value="">
                        {t("all")} {t("types")}
                      </option>
                      {bookingTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div> */}

                {/* Charter Frequency */}
                <div className="col-md-6">
                  <div className="filter-group">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-redo me-2"></i>
                      {t("charter")} {t("frequency")}
                    </label>
                    <select
                      className="form-select"
                      value={filters.charter_frequency}
                      onChange={(e) =>
                        handleFilterChange("charter_frequency", e.target.value)
                      }
                    >
                      <option value="">
                        {t("all")} {t("frequencies")}
                      </option>
                      {charterFrequencyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Charter Type */}
                <div className="col-md-6">
                  <div className="filter-group">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-route me-2"></i>
                      {t("charter")} {t("type")}
                    </label>
                    <select
                      className="form-select"
                      value={filters.charter_type}
                      onChange={(e) =>
                        handleFilterChange("charter_type", e.target.value)
                      }
                    >
                      <option value="">
                        {t("all")} {t("charter")} {t("types")}
                      </option>
                      {charterTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Filter Actions */}
                <div className="col-md-6 align-self-end ms-auto">
                  <div className="filter-group d-flex gap-2 w-100">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={handleReset}
                    >
                      <TbFilterOff className="me-2" />
                      {t("btns.reset")} {t("all")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary flex-fill"
                      onClick={() => handleApplyFilters(filters)}
                    >
                      <i className="fas fa-check me-2"></i>
                      {t("btns.apply")} {t("btns.filters")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {(filters.status ||
              filters.booking_type ||
              filters.charter_frequency ||
              filters.charter_type) && (
              <div className="active-filters mt-4">
                <div className="d-flex align-items-center mb-2">
                  <small className="text-muted me-2">
                    <i className="fas fa-filter me-1"></i>
                    {t("active")} {t("filters")}:
                  </small>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {filters.status && (
                    <span className="badge bg-secondary d-flex align-items-center">
                      {t("status")}:{" "}
                      {
                        statusOptions.find((o) => o.value === filters.status)
                          ?.label
                      }
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "10px" }}
                        onClick={() => handleFilterChange("status", "")}
                      />
                    </span>
                  )}
                  {filters.booking_type && (
                    <span className="badge bg-secondary d-flex align-items-center">
                      {t("booking")} {t("type")}:{" "}
                      {
                        bookingTypeOptions.find(
                          (o) => o.value === filters.booking_type,
                        )?.label
                      }
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "10px" }}
                        onClick={() => handleFilterChange("booking_type", "")}
                      />
                    </span>
                  )}
                  {filters.charter_frequency && (
                    <span className="badge bg-secondary d-flex align-items-center">
                      {t("frequency")}:{" "}
                      {
                        charterFrequencyOptions.find(
                          (o) => o.value === filters.charter_frequency,
                        )?.label
                      }
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "10px" }}
                        onClick={() =>
                          handleFilterChange("charter_frequency", "")
                        }
                      />
                    </span>
                  )}
                  {filters.charter_type && (
                    <span className="badge bg-secondary d-flex align-items-center">
                      {t("charter")} {t("type")}:{" "}
                      {
                        charterTypeOptions.find(
                          (o) => o.value === filters.charter_type,
                        )?.label
                      }
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "10px" }}
                        onClick={() => handleFilterChange("charter_type", "")}
                      />
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* Filter Component Styles */
        .filter-panel {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #e0e0e0;
          overflow: hidden;
        }

        .filter-header {
              background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
          color: white;
          padding: 1rem 1.5rem;
          display: flex;
    justify-content: space-between;
        align-items: center;
        }

        .filter-body {
          padding: 1.5rem;
        }

        .quick-search-section .search-box-wrapper {
          position: relative;
        }

        .quick-search-section .input-group-text {
          background: #f8f9fa;
          border-right: none;
        }

        .quick-search-section .form-control {
          border-left: none;
          padding-left: 0;
        }

        .quick-search-section .form-control:focus {
          box-shadow: none;
          border-color: #ced4da;
        }

        .status-filter-section .status-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .status-btn {
          padding: 6px 16px;
          border: 2px solid #dee2e6;
          border-radius: 20px;
          background: white;
          color: #495057;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .status-btn:hover {
          border-color: #6c757d;
          color: #495057;
        }

        .status-btn.active {
          background: #f0b342;
          border-color: #f0b342;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
        }

        .advanced-toggle .btn-link,.advanced-toggle .btn-link:hover,.advanced-toggle .btn:active {
          color: #6c757d;
          font-weight: 500;
        }

        .advanced-toggle .btn-link:focus-visible {
         box-shadow:none !important;
        }

        .advanced-toggle .btn:hover, .btn:active:hover {
    background: transparent !important;
    border-color: transparent !important;
}

        .advanced-filters {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 0;
          transition: max-height 0.4s ease;
        }

        .advanced-filters.show {
          padding: 1.5rem;
          border: 1px solid #e9ecef;
        }

        .filter-group {
          margin-bottom: 1rem;
        }

        .filter-group .form-select {
          border-radius: 8px;
          border: 1px solid #ced4da;
          padding: 10px 15px;
          transition: all 0.2s ease;
        }

        .filter-group .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .active-filters {
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px dashed #dee2e6;
        }

        .active-filters .badge {
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 500;
          border-radius: 20px;
        }

        .active-filters .btn-close {
          opacity: 0.8;
          width: 12px;
          height: 12px;
        }

        .active-filters .btn-close:hover {
          opacity: 1;
        }

        .custom-collapse {
          overflow: hidden;
          transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .filter-body {
            padding: 1rem;
          }
          
          .status-buttons {
            justify-content: center;
          }
          
          .status-btn {
            flex: 1;
            // min-width: 80px;
            text-align: center;
          }
          
          .advanced-filters.show {
            padding: 1rem;
          }
          
          .filter-header h5 {
            font-size: 1.1rem;
          }
        }

        /* Animation for filter panel */
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .filter-panel {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default FilterComponent;
